import { Component, OnInit, Input, Output, OnDestroy, EventEmitter, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ToastService } from 'src/app/services/toast.service';
import { DateRange } from 'src/app/models/lt-daterange.model';
import { LTDateRangeMonthYearComponent } from '../lt-daterange-month-year/lt-daterange-month-year.component';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
const today = new Date();
const month = today.getMonth();
const year = today.getFullYear();
const day = today.getDate();

@Component({
  selector: 'app-date-range-view',
  templateUrl: './date-range-view.component.html',
  styleUrls: ['./date-range-view.component.scss'],
  imports: [
    ReactiveFormsModule,
    LTDateRangeMonthYearComponent,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    CommonModule,
  ],
})
export class DateRangeViewComponent implements OnInit, OnDestroy {
  componentDestroyed$ = new Subject<boolean>();

  @ViewChild('ltDateRange') public ltDateRange: LTDateRangeMonthYearComponent;

  @Input() monthYearOnly = false;
  @Input() showThisMonthButton = true;
  @Input() dateStringPrefix = '';
  @Output() updatedRange = new EventEmitter<[Date,Date]>();
  @Input() initDateRange: [Date,Date] = null;

  hasDateRangeSelected = false;

  currentStartDate: Date = null;
  currentEndDate: Date = null;

  // loan
  public range = new FormGroup({
    start: new FormControl<Date | null>(new Date(year, month, 1)),
    end: new FormControl<Date | null>(new Date(year, month, day)),
  });

  // metric
  public metricRange = new FormGroup({
    dateRange: new FormControl<DateRange>({
      start: new Date(year, month - 1, 1),
      end: new Date(year, month, 0),
    }),
  });

  constructor(protected toastService: ToastService) { }

  ngOnInit(): void {
    this.currentEndDate = null;
    this.currentStartDate = null;
    this.hasDateRangeSelected = false;

    // loan
    this.range.setValue({
      start: new Date(year, month, 1),
      end: new Date(year, month, day),
    });

    // metric
    this.ltDateRange.writeValue({
      start: new Date(year, month - 1, 1),
      end: new Date(year, month, 0),
    });

    if (this.initDateRange !== null && this.initDateRange !== undefined) {
      this.sendDateRange(this.initDateRange[0], this.initDateRange[1]);

      // test if this sends output twice

      if (this.monthYearOnly) {
        this.ltDateRange.writeValue({
          start: this.initDateRange[0],
          end: this.initDateRange[1],
        });
      }
      else {
        this.range.setValue({
          start: this.initDateRange[0],
          end: this.initDateRange[1],
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  clear(): void {
    this.currentEndDate = null;
    this.currentStartDate = null;
    this.hasDateRangeSelected = false;

    // loan
    this.range.setValue({
      start: new Date(year, month, 1),
      end: new Date(year, month, day),
    });

    // metric
    this.ltDateRange.writeValue({
      start: new Date(year, month - 1, 1),
      end: new Date(year, month, 0),
    });
  }

  submitDateRange() {
    this.hasDateRangeSelected = false;
    let startDate: Date = null;
    let endDate: Date = null;

    if (this.monthYearOnly) {
      const dateRangeValue = this.metricRange.get('dateRange')?.value as DateRange;
      
      if (this.metricRange.get('dateRange')?.valid && dateRangeValue) {
        startDate = dateRangeValue.start;
        endDate = dateRangeValue.end;
      }
    }
    else {
      // if (this.range.get('start').status == 'VALID') {
      //   startDate = new Date(this.range.get('start').value);
      // }

      // if (this.range.get('end').status == 'VALID') {
      //   endDate = new Date(this.range.get('end').value);
      // }
      if (this.range.get('start')?.valid) {
        startDate = this.range.get('start')?.value;
      }

      if (this.range.get('end')?.valid) {
        endDate = this.range.get('end')?.value;
      }
    }

    if (startDate == null || endDate == null) {
      this.toastService.toastCreate('Date range invalid', 'Warning');
    }
    else {
      this.sendDateRange(startDate, endDate);
    }
  }

  sendDateRange(start: Date, end: Date): void {
    this.currentStartDate = start;
    this.currentEndDate = end;
    this.hasDateRangeSelected = true;
    this.updatedRange.emit([this.currentStartDate, this.currentEndDate]);
  }

  submitDefaultRange(event: any) {
    this.hasDateRangeSelected = false;
    let startDate: Date = null;
    const lastDayOfLastMonth: Date = new Date(today.getFullYear(), today.getMonth(), 0);

    if (event == 'yearToDate') {
      startDate = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      this.sendDateRange(startDate, today);
    }
    else if (event == 'thisYear') {
      startDate = new Date(new Date().getUTCFullYear(), 0, 1, 1);
      this.sendDateRange(startDate, today);
    }
    else if (event == 'past3Months') {
      if (this.monthYearOnly) {
        startDate = new Date(today.getFullYear(), today.getMonth() - 4, 1);
        this.sendDateRange(startDate, lastDayOfLastMonth);
      }
      else {
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, today.getDate());
        this.sendDateRange(startDate, today);
      }
    }
    else if (event == 'lastMonth') {
      if (this.monthYearOnly) {
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        this.sendDateRange(startDate, lastDayOfLastMonth);
      }
      else {
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
        this.sendDateRange(startDate, today);
      }
    }
    else if (event == 'thisMonth') {
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      const endDate: Date = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      this.sendDateRange(startDate, endDate);
    }

    // Update the form controls based on the preset range
    if (this.monthYearOnly) {
      this.ltDateRange.writeValue({
        start: this.currentStartDate,
        end: this.currentEndDate,
      });
    } else {
      this.range.setValue({
        start: this.currentStartDate,
        end: this.currentEndDate,
      });
    }
  }

}
